import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';

import type { AuthErrorBody } from '@vevents/auth';

import { AuthService } from '../services/auth.service';

/**
 * MOB-100 Stage 18 — mobile auth interceptor.
 *
 * Two responsibilities, one per phase of the request lifecycle:
 *
 * 1. **Request out**: attach `Authorization: Bearer <accessToken>` and the
 *    `X-Mobile-Device-Session: <deviceSessionId>` header so the server
 *    can key the sudo cache. We do NOT attach the header to auth-self
 *    calls (`/api/auth/resolve-token`, `/api/auth/refresh`, `/api/auth/login`)
 *    because they are unauthenticated entry points and Stage 2 considers
 *    them rate-limited at the gateway instead.
 *
 * 2. **Response in**: on a `401` from an authenticated endpoint, call
 *    `AuthService.refresh()`. The refresh implementation is single-
 *    flight so a burst of parallel 401s collapses to one refresh round-
 *    trip, after which every queued request replays exactly once.
 *
 * The replay strategy: rerun the original request through `next()` after
 * marking it `SKIP_AUTH_RETRY=true`. Because the refresh has already
 * swapped the in-memory bearer, `attachAuthHeaders()` picks up the fresh
 * token automatically when the cloned request flows through this same
 * interceptor on its second pass.
 *
 * The sudo interceptor must run BEFORE this one — `403 sudo-required` is
 * its concern. Order in `withInterceptors([...])` is load-bearing; see
 * `app.config.ts`.
 */
const SKIP_AUTH_RETRY = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  // Respect an Authorization header the caller set explicitly. The
  // `YourEventsService` per-tenant fan-out targets a NON-active tenant's
  // subdomain with that tenant's bearer; overwriting it with the active
  // tenant's token via `attachAuthHeaders()` made every tenant section in
  // "Your events" render the ACTIVE tenant's events (e.g. a FrontM event
  // showing under the Virtrio section). When the caller has already chosen
  // the bearer we neither replace it on the way out nor hijack a 401 with
  // the active tenant's refresh — the caller owns that request's identity.
  const callerSetAuth = req.headers.has('Authorization');
  const authed =
    isAuthEndpoint(req.url) || callerSetAuth ? req : attachAuthHeaders(req, auth);

  return next(authed).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }
      if (isAuthEndpoint(req.url) || callerSetAuth) {
        return throwError(() => err);
      }
      // Tenant mismatch (Stage 7) is not a refresh signal — the access
      // token is valid; the subdomain isn't. Surface to caller.
      if (readErrorCode(err) === 'token-tenant-mismatch') {
        return throwError(() => err);
      }
      // Already-marked-retried requests must NOT trigger a second refresh.
      if (req.context.get(SKIP_AUTH_RETRY)) {
        return throwError(() => err);
      }

      return from(auth.refresh()).pipe(
        switchMap(() => {
          const retried = attachAuthHeaders(req, auth, /* markRetried */ true);
          return next(retried);
        }),
        catchError((refreshErr: unknown) => throwError(() => refreshErr ?? err))
      );
    })
  );
};

function attachAuthHeaders(
  req: HttpRequest<unknown>,
  auth: AuthService,
  markRetried = false
): HttpRequest<unknown> {
  const token = auth.getToken();
  if (!token) {
    return markRetried
      ? req.clone({ context: req.context.set(SKIP_AUTH_RETRY, true) })
      : req;
  }

  const setHeaders: Record<string, string> = { Authorization: `Bearer ${token}` };
  const deviceSessionId = auth.getActiveDeviceSessionId();
  if (deviceSessionId != null) {
    setHeaders['X-Mobile-Device-Session'] = String(deviceSessionId);
  }
  return req.clone({
    setHeaders,
    context: markRetried ? req.context.set(SKIP_AUTH_RETRY, true) : req.context
  });
}

function isAuthEndpoint(url: string): boolean {
  const u = url || '';
  return (
    u.includes('/api/auth/login') ||
    u.includes('/api/auth/refresh') ||
    u.includes('/api/auth/resolve-token') ||
    u.includes('/api/auth/device-verification/')
  );
}

function readErrorCode(err: HttpErrorResponse): string | null {
  const body = err.error as Partial<AuthErrorBody> | null | undefined;
  if (!body || typeof body !== 'object') return null;
  return typeof body.errorCode === 'string' ? body.errorCode : null;
}
